import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { extractMemberFromBettie } from './utils/extractMemberFromBettie';
import { useBettieSearch } from '../../domains/bettie/ui/hooks/useBettieSearch';
import { useBettieProfileImageOverlay } from '../chat/hooks/useBettieProfileImageOverlay';
import { usePersonaManager } from './hooks/usePersonaManager';
import { useSceneStepFlow } from './hooks/useSceneStepFlow';
import SceneLogo from './components/SceneLogo';
import AddSceneArtistsBox from './components/AddSceneArtistsBox';
import LoadingSceneStep from './components/LoadingSceneStep';
import PersonaInputStep from './components/PersonaInputStep';
import EnterArtistInfoStep from './components/EnterArtistInfoStep';
import EnterUserInfoStep from './components/EnterUserInfoStep';
import BettieProfileImageOverlay from '../chat/components/BettieProfileImageOverlay';

const SceneSettingPage = () => {
  const handleHistoryBack = useHandleHistoryBack();

  const flow = useSceneStepFlow();

  const persona = usePersonaManager({
    closeBottomSheet: flow.closeBottomSheet
  });

  const cancelPersonaFlow = () => {
    persona.actions.cancelPersonaFlow(
      persona.state.activePersonaSlot === 'second' ? 'secondPersonaInput' : 'firstPersonaSelected'
    );
    flow.closeBottomSheet();
  };

  const [firstNickname, setFirstNickname] = useState('');
  const [secondNickname, setSecondNickname] = useState('');
  const firstBettieSearch = useBettieSearch(firstNickname.trim(), { enabled: false });
  const secondBettieSearch = useBettieSearch(secondNickname.trim(), { enabled: false });

  const firstPhotoOverlay = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl }) => persona.actions.setPersonaDataUrl('first', dataUrl)
  });
  const secondPhotoOverlay = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl }) => persona.actions.setPersonaDataUrl('second', dataUrl)
  });

  const handleResetCombination = () => {
    persona.actions.resetPersonas();
    setFirstNickname('');
    setSecondNickname('');
  };

  const handleFirstNext = async () => {
    const trimmed = firstNickname.trim();
    if (!trimmed) return;
    const { data } = await firstBettieSearch.refetch();
    const bettieData = data?.bettie;
    const memberInfo = extractMemberFromBettie(bettieData);
    if (bettieData && memberInfo) {
      persona.actions.handlePersonaFromSearchBySlot(
        {
          kind: 'artist',
          persona: {
            nickname: trimmed,
            dataUrl: '',
            memberPersonaId: (bettieData as { memberPersonaId?: string }).memberPersonaId,
            memberId: memberInfo.memberId,
            memberName: memberInfo.memberName
          }
        },
        'first'
      );
    } else {
      persona.actions.handlePersonaFromSearchBySlot(
        { kind: 'celeb', persona: { nickname: trimmed, dataUrl: '' } },
        'first'
      );
    }
  };

  const handleSecondNext = async () => {
    const trimmed = secondNickname.trim();
    if (!trimmed) return;
    const { data } = await secondBettieSearch.refetch();
    const bettieData = data?.bettie;
    const memberInfo = extractMemberFromBettie(bettieData);
    if (bettieData && memberInfo) {
      persona.actions.handlePersonaFromSearchBySlot(
        {
          kind: 'artist',
          persona: {
            nickname: trimmed,
            dataUrl: '',
            memberPersonaId: (bettieData as { memberPersonaId?: string }).memberPersonaId,
            memberId: memberInfo.memberId,
            memberName: memberInfo.memberName
          }
        },
        'second'
      );
    } else {
      persona.actions.handlePersonaFromSearchBySlot(
        { kind: 'celeb', persona: { nickname: trimmed, dataUrl: '' } },
        'second'
      );
    }
  };

  if (persona.state.step === 'firstPersonaSelected') {
    return (
      <PersonaInputStep
        slot="first"
        onBack={handleHistoryBack}
        onSetAsMe={() => persona.actions.handleSetSlotAsUser('first')}
        nickname={firstNickname}
        onNicknameChange={setFirstNickname}
        onNext={handleFirstNext}
      />
    );
  }

  if (persona.state.step === 'secondPersonaInput') {
    return (
      <PersonaInputStep
        slot="second"
        onBack={() => persona.actions.setStep('firstPersonaSelected')}
        onSetAsMe={() => persona.actions.handleSetSlotAsUser('second')}
        nickname={secondNickname}
        onNicknameChange={setSecondNickname}
        onNext={handleSecondNext}
        isFirstPersonaUser={persona.state.firstPersona?.entityType === 'User'}
        firstPersonaNickname={persona.state.firstPersona?.nickname ?? ''}
      />
    );
  }

  if (persona.state.step === 'loadingScene') {
    return (
      <LoadingSceneStep
        firstPersona={persona.state.firstPersona}
        secondPersona={persona.state.secondPersona}
      />
    );
  }

  if (persona.state.step === 'enterArtistInfo') {
    return (
      <EnterArtistInfoStep
        nickname={persona.state.pendingPersona?.nickname ?? ''}
        onBack={cancelPersonaFlow}
        onSubmit={persona.actions.handleEnterArtistInfoSubmit}
      />
    );
  }

  if (persona.state.step === 'enterUserInfo') {
    return (
      <EnterUserInfoStep
        initialNickname={persona.state.pendingPersona?.nickname ?? ''}
        initialDataUrl={persona.state.pendingPersona?.dataUrl}
        onBack={cancelPersonaFlow}
        onSubmit={persona.actions.handleEnterUserInfoSubmit}
      />
    );
  }

  return (
    <div
      className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      style={{
        backgroundImage: 'url(/assets/scene/bg_scene_setting.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <Helmet>
        <title>씬</title>
      </Helmet>
      <header className="px-[16px] py-[15px]">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>
      <main className="flex flex-col items-center">
        <div className="w-full flex flex-col items-center">
          <SceneLogo className="pt-[72px]" />
          <div className="mt-[57px]">
            <p className="text-[20px] font-semibold leading-[24px] text-pure-white">
              이 조합으로 시작할까요?
            </p>
          </div>
        </div>

        <AddSceneArtistsBox
          firstPersona={persona.state.firstPersona}
          secondPersona={persona.state.secondPersona}
          onAddSceneArtist={(slot) =>
            slot === 'first'
              ? firstPhotoOverlay.openImagePicker()
              : secondPhotoOverlay.openImagePicker()
          }
        />
      </main>
      <input {...firstPhotoOverlay.fileInputProps} />
      <input {...secondPhotoOverlay.fileInputProps} />
      <BettieProfileImageOverlay {...firstPhotoOverlay.overlayProps} />
      <BettieProfileImageOverlay {...secondPhotoOverlay.overlayProps} />
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+16px)] flex flex-col items-center gap-[20px]">
        <Button
          size="large"
          disabled={!persona.state.canProceedNext}
          className="w-full text-[17px] font-semibold disabled:bg-disabled-dark disabled:text-onDisabled-dark"
          onClick={persona.actions.handleNext}
        >
          이대로 시작하기
        </Button>
        <button
          type="button"
          className="text-[15px] font-medium leading-[18px] text-pure-white"
          onClick={handleResetCombination}
        >
          조합 다시 만들기
        </button>
      </div>
    </div>
  );
};

export default SceneSettingPage;
